/**
 * Image Uploader Component
 *
 * Soporta:
 * 1. Upload a Cloudinary (client-side, unsigned) — preferido para portfolio
 * 2. Upload a Cloudflare Images (API backend) — fallback
 * 3. Pegar URL o public_id directamente
 * 4. Explorar assets existentes en Cloudinary (tab "Explorar")
 *
 * El componente NO muestra toasts de éxito al subir — el consumidor recibe
 * la URL via onImageAdded/onBulkImagesAdded y puede dar su propio feedback.
 */

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import {
  buildCloudinaryFolder,
  getCloudinaryThumbUrl,
  type CloudinaryAsset,
} from "@/lib/cloudinaryUtils";
import {
  FolderSearch,
  Upload,
  Link as LinkIcon,
  Loader2,
  CheckCircle2,
  ChevronDown,
} from "lucide-react";
import { useState, useRef, useCallback, useEffect, useMemo } from "react";

interface ImageUploaderProps {
  onImageAdded: (url: string) => void;
  onBulkImagesAdded?: (urls: string[]) => void;
  /** Preferir Cloudinary cuando ambos están configurados (ej: portfolio) */
  preferCloudinary?: boolean;
  /** Carpeta / título de sección para la ruta Cloudinary */
  cloudinaryFolder?: string;
}

const CLOUDINARY_CLOUD = (import.meta.env.VITE_CLOUDINARY_CLOUD_NAME as string)?.trim() || "";
const CLOUDINARY_PRESET = (import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET as string)?.trim() || "";
const CLOUDFLARE_ACCOUNT_HASH = (import.meta.env.VITE_CLOUDFLARE_ACCOUNT_HASH as string)?.trim() || "";

const CLOUDINARY_ENABLED = !!(CLOUDINARY_CLOUD && CLOUDINARY_PRESET);
const CLOUDFLARE_ENABLED = !!CLOUDFLARE_ACCOUNT_HASH;

export function ImageUploader({
  onImageAdded,
  onBulkImagesAdded,
  preferCloudinary = false,
  cloudinaryFolder,
}: ImageUploaderProps) {
  const { toast } = useToast();
  const { getAuthHeaders } = useAdminAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [bulkUrls, setBulkUrls] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Explorar Cloudinary
  const [browseAssets, setBrowseAssets] = useState<CloudinaryAsset[]>([]);
  const [browseCursor, setBrowseCursor] = useState<string | null>(null);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browsedOnce, setBrowsedOnce] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());

  // AbortController para cancelar requests de browse en vuelo si el componente desmonta
  const browseAbortRef = useRef<AbortController | null>(null);
  // Ref-level guard to prevent concurrent initial loads (state updates are async)
  const browseLoadingRef = useRef(false);

  useEffect(() => {
    return () => {
      browseAbortRef.current?.abort();
    };
  }, []);

  const uploadEnabledForContext = preferCloudinary
    ? CLOUDINARY_ENABLED
    : CLOUDINARY_ENABLED || CLOUDFLARE_ENABLED;
  const useCloudinary = CLOUDINARY_ENABLED;

  // Derived once per render — cheap string op, no need for useMemo.
  const folderPrefix = buildCloudinaryFolder(cloudinaryFolder);

  // Total tab count drives the Tailwind grid-cols class.
  const tabCount = useMemo(
    () =>
      (uploadEnabledForContext ? 1 : 0) +
      1 + // URL
      1 + // Múltiples
      (CLOUDINARY_ENABLED ? 1 : 0),
    [uploadEnabledForContext],
  );

  const ensureCloudinaryPreset = async (): Promise<{ ok: boolean; message?: string }> => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/cloudinary/ensure-preset", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) return { ok: true };
      return { ok: false, message: data.message || data.error || `HTTP ${res.status}` };
    } catch (e) {
      return { ok: false, message: e instanceof Error ? e.message : "Error de red" };
    }
  };

  const uploadToCloudinaryBackend = async (file: File, headers: Record<string, string>): Promise<string> => {
    const reader = new FileReader();
    const base64 = await new Promise<string>((resolve, reject) => {
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const res = await fetch("/api/cloudinary/upload", {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ image: base64, filename: file.name, folder: folderPrefix }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || err.error || "Upload fallback falló");
    }
    const data = await res.json();
    return data.url;
  };

  const uploadToCloudinary = async (file: File, headers: Record<string, string>): Promise<string> => {
    const doDirectUpload = async (): Promise<string> => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("upload_preset", CLOUDINARY_PRESET);
      fd.append("folder", folderPrefix);

      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/image/upload`,
        { method: "POST", body: fd }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const msg = err.error?.message || "Cloudinary upload failed";
        if (msg.toLowerCase().includes("upload preset not found")) {
          const result = await ensureCloudinaryPreset();
          if (result.ok) return doDirectUpload();
        }
        throw new Error(msg);
      }

      const data = await res.json();
      return data.secure_url || data.public_id || data.url;
    };

    try {
      return await doDirectUpload();
    } catch (directErr) {
      try {
        return await uploadToCloudinaryBackend(file, headers);
      } catch (backendErr) {
        const backendMsg = backendErr instanceof Error ? backendErr.message : String(backendErr);
        const directMsg = directErr instanceof Error ? directErr.message : String(directErr);
        throw new Error(
          `Upload directo falló: ${directMsg}. Fallback backend: ${backendMsg}. Verificá CLOUDINARY_API_KEY y CLOUDINARY_API_SECRET en Railway.`
        );
      }
    }
  };

  const uploadToCloudflare = async (file: File, headers: Record<string, string>): Promise<string> => {
    const reader = new FileReader();
    const base64 = await new Promise<string>((resolve, reject) => {
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const response = await fetch("/api/upload-image", {
      method: "POST",
      headers,
      body: JSON.stringify({ image: base64, filename: file.name }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to upload image");
    }
    const data = await response.json();
    return data.url;
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setIsUploading(true);
    try {
      // Fetch token once — reused for all parallel uploads to avoid N Clerk round-trips.
      const headers = await getAuthHeaders();
      const uploadFn = useCloudinary ? uploadToCloudinary : uploadToCloudflare;
      const urls = await Promise.all(Array.from(files).map((file) => uploadFn(file, headers)));

      if (urls.length === 1) {
        onImageAdded(urls[0]);
      } else {
        if (onBulkImagesAdded) { onBulkImagesAdded(urls); } else { urls.forEach((url) => onImageAdded(url)); }
      }
      // No toast here — the parent callback provides upload/save feedback.
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      console.error("Error uploading images:", error);
      toast({
        title: "Error al subir",
        description: error instanceof Error ? error.message : "No se pudieron subir las imágenes",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const isValidImageInput = (s: string): boolean =>
    s.startsWith("http://") ||
    s.startsWith("https://") ||
    (s.length > 1 && !s.includes(" ") && !s.includes("\n"));

  const handleUrlAdd = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    if (!isValidImageInput(trimmed)) {
      toast({
        title: "Entrada inválida",
        description: "Usa URL completa (http...) o public_id de Cloudinary",
        variant: "destructive",
      });
      return;
    }
    onImageAdded(trimmed);
    setUrlInput("");
  };

  const handleBulkUrlAdd = () => {
    if (!bulkUrls.trim()) return;
    const urls = bulkUrls
      .split(/[\n,;]/)
      .map((url) => url.trim())
      .filter((url) => isValidImageInput(url));
    if (urls.length === 0) {
      toast({
        title: "No hay entradas válidas",
        description: "Usa URLs (http...) o public_ids de Cloudinary",
        variant: "destructive",
      });
      return;
    }
    if (onBulkImagesAdded) { onBulkImagesAdded(urls); } else { urls.forEach((url) => onImageAdded(url)); }
    setBulkUrls("");
  };

  // ── Explorar Cloudinary ──────────────────────────────────────────────────────

  const loadBrowse = useCallback(async (cursor?: string) => {
    // Ref-level guard prevents concurrent initial loads triggered by rapid tab clicks
    if (!cursor && browseLoadingRef.current) return;
    browseLoadingRef.current = true;

    // Cancel any previous in-flight request
    browseAbortRef.current?.abort();
    const ac = new AbortController();
    browseAbortRef.current = ac;

    setBrowseLoading(true);
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({ prefix: folderPrefix });
      if (cursor) params.set("next_cursor", cursor);
      const res = await fetch(`/api/cloudinary/list-folder?${params.toString()}`, {
        headers,
        signal: ac.signal,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      const data = await res.json() as { assets: CloudinaryAsset[]; next_cursor: string | null };
      // Cap total in-memory assets at 300 to prevent unbounded growth
      setBrowseAssets((prev) => {
        const combined = cursor ? [...prev, ...data.assets] : data.assets;
        return combined.slice(0, 300);
      });
      setBrowseCursor(data.next_cursor);
      setBrowsedOnce(true);
    } catch (err) {
      if ((err as { name?: string }).name === "AbortError") return; // component unmounted
      toast({
        title: "Error al explorar Cloudinary",
        description: err instanceof Error ? err.message : "Error desconocido",
        variant: "destructive",
      });
    } finally {
      setBrowseLoading(false);
      browseLoadingRef.current = false;
    }
  }, [getAuthHeaders, folderPrefix, toast]);

  const toggleAssetSelect = (asset: CloudinaryAsset) => {
    setSelectedAssets((prev) => {
      const next = new Set(prev);
      if (next.has(asset.secure_url)) next.delete(asset.secure_url);
      else next.add(asset.secure_url);
      return next;
    });
  };

  const handleAddSelected = () => {
    const urls = [...selectedAssets];
    if (urls.length === 0) return;
    // Fall back to onImageAdded per-URL if onBulkImagesAdded isn't provided
    if (urls.length === 1 || !onBulkImagesAdded) {
      urls.forEach((url) => onImageAdded(url));
    } else {
      onBulkImagesAdded(urls);
    }
    setSelectedAssets(new Set());
    // No separate success toast — parent callback handles feedback
  };

  return (
    <Tabs defaultValue={uploadEnabledForContext ? "upload" : "url"} className="w-full">
      <TabsList className={`grid w-full grid-cols-${tabCount}`}>
        {uploadEnabledForContext && (
          <TabsTrigger value="upload">
            <Upload className="w-4 h-4 mr-2" />
            Subir
          </TabsTrigger>
        )}
        <TabsTrigger value="url">
          <LinkIcon className="w-4 h-4 mr-2" />
          URL
        </TabsTrigger>
        <TabsTrigger value="bulk">
          <LinkIcon className="w-4 h-4 mr-2" />
          Múltiples
        </TabsTrigger>
        {CLOUDINARY_ENABLED && (
          <TabsTrigger
            value="browse"
            onClick={() => { if (!browsedOnce && !browseLoadingRef.current) loadBrowse(); }}
          >
            <FolderSearch className="w-4 h-4 mr-2" />
            Explorar
          </TabsTrigger>
        )}
      </TabsList>

      {/* Upload Tab */}
      {uploadEnabledForContext && (
        <TabsContent value="upload" className="space-y-4">
          <div className="space-y-2">
            <Label>Subir Imágenes</Label>
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handleFileUpload(e.target.files)}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                variant="outline"
                className="w-full"
              >
                {isUploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Subiendo...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" />Seleccionar Imágenes</>
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {useCloudinary
                ? "Podés seleccionar múltiples imágenes. Se subirán a Cloudinary automáticamente."
                : "Podés seleccionar múltiples imágenes. Se subirán a Cloudflare Images automáticamente."}
            </p>
          </div>
        </TabsContent>
      )}

      {/* URL Tab */}
      <TabsContent value="url" className="space-y-4">
        <div className="space-y-2">
          <Label>URL de la Imagen</Label>
          <div className="flex gap-2">
            <Input
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="portfolio/foto1 o https://res.cloudinary.com/..."
              onKeyDown={(e) => { if (e.key === "Enter") handleUrlAdd(); }}
            />
            <Button onClick={handleUrlAdd} variant="outline">Agregar</Button>
          </div>
          <p className="text-xs text-muted-foreground">
            URL completa o public_id Cloudinary (ej:{" "}
            <code className="bg-muted px-1 rounded">portfolio/foto1</code>)
          </p>
        </div>
      </TabsContent>

      {/* Bulk URLs Tab */}
      <TabsContent value="bulk" className="space-y-4">
        <div className="space-y-2">
          <Label>Múltiples URLs</Label>
          <textarea
            value={bulkUrls}
            onChange={(e) => setBulkUrls(e.target.value)}
            placeholder={"URLs o public_ids (una por línea)\nportfolio/foto1\nportfolio/foto2\nhttps://res.cloudinary.com/..."}
            className="w-full px-3 py-2 border rounded-md min-h-[120px] text-sm font-mono"
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {bulkUrls.split(/[\n,;]/).filter((url) => isValidImageInput(url.trim())).length} entradas válidas
            </p>
            <Button onClick={handleBulkUrlAdd} size="sm">Agregar URLs</Button>
          </div>
        </div>
      </TabsContent>

      {/* Browse Cloudinary Tab */}
      {CLOUDINARY_ENABLED && (
        <TabsContent value="browse" className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Label>Explorar Cloudinary</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Carpeta: <code className="bg-muted px-1 rounded">{folderPrefix}</code>
              </p>
            </div>
            <div className="flex items-center gap-2">
              {selectedAssets.size > 0 && (
                <Button size="sm" onClick={handleAddSelected}>
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Agregar {selectedAssets.size}
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => loadBrowse()}
                disabled={browseLoading}
              >
                {browseLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Actualizar"}
              </Button>
            </div>
          </div>

          {browseLoading && browseAssets.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Cargando imágenes...
            </div>
          ) : browsedOnce && browseAssets.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-6">
              No hay imágenes en esta carpeta todavía.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-72 overflow-y-auto pr-1">
                {browseAssets.map((asset) => {
                  const selected = selectedAssets.has(asset.secure_url);
                  return (
                    <button
                      key={asset.public_id}
                      type="button"
                      onClick={() => toggleAssetSelect(asset)}
                      className={`relative rounded-md overflow-hidden border-2 transition-all aspect-square ${
                        selected
                          ? "border-primary ring-2 ring-primary/40"
                          : "border-transparent hover:border-muted-foreground/30"
                      }`}
                      title={asset.public_id}
                    >
                      <img
                        src={getCloudinaryThumbUrl(asset.secure_url, 160)}
                        alt={asset.public_id}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      {selected && (
                        <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                          <CheckCircle2 className="w-6 h-6 text-primary-foreground drop-shadow" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {browseCursor && browseAssets.length < 300 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={() => loadBrowse(browseCursor)}
                  disabled={browseLoading}
                >
                  {browseLoading
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Cargando...</>
                    : <><ChevronDown className="w-4 h-4 mr-2" />Cargar más</>}
                </Button>
              )}
            </>
          )}
        </TabsContent>
      )}
    </Tabs>
  );
}
